import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button, Progress, Badge, Card } from '@nimbitlabs/ui';
import { OnboardingLoadingSkeleton } from '@/components/OnboardingLoadingSkeleton';
import { 
  CheckCircle2, 
  Circle, 
  X,
  Package,
  Users,
  Globe,
  Activity,
  Trophy,
  Star,
  Zap,
  Target,
  Award,
  ChevronRight
} from 'lucide-react';
import { cn } from '@nimbitlabs/ui';
import { useServicesStore } from '@/stores/servicesStore';
import { useTeamsStore } from '@/stores/teamsStore';
import { useEnvironmentsStore } from '@/stores/environmentsStore';
import { useAuth } from '@nimbitlabs/auth';
import { useOnboardingStore } from '@/stores/onboardingStore';

interface ChecklistItem {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  link: string;
  completed: boolean;
}

interface OnboardingChecklistProps {
  hasMonitoringConfigs?: boolean;
}

export const OnboardingChecklist: React.FC<OnboardingChecklistProps> = ({ hasMonitoringConfigs = false }) => {
  const { user } = useAuth();
  const currentAccountId = user?.accountId;
  const [isOpen, setIsOpen] = useState(false);
  const [isDismissed, setIsDismissed] = useState(() => {
    if (!currentAccountId) return false;
    return useOnboardingStore.getState().isChecklistDismissedForAccount(currentAccountId);
  });

  // Read from stores - DO NOT fetch, GamificationPanel handles fetching
  const services = useServicesStore(state => state.services);
  const teams = useTeamsStore(state => state.teams);
  const environments = useEnvironmentsStore(state => state.environments);
  
  const servicesLoading = useServicesStore(state => state.loading);
  const teamsLoading = useTeamsStore(state => state.loading);
  const environmentsLoading = useEnvironmentsStore(state => state.loading);

  // Reset dismissed state when account changes
  useEffect(() => {
    if (currentAccountId) {
      const dismissed = useOnboardingStore.getState().isChecklistDismissedForAccount(currentAccountId);
      setIsDismissed(dismissed);
    }
  }, [currentAccountId]);

  const checklist: ChecklistItem[] = [
    {
      id: 'create_environment',
      title: 'Add an Environment',
      description: 'Define your deployment environments',
      icon: Globe,
      link: '/environments/new',
      completed: environments.length > 0,
    },
    {
      id: 'create_team',
      title: 'Set up a Team',
      description: 'Organize your services by team',
      icon: Users,
      link: '/teams/new',
      completed: teams.length > 0,
    },
    {
      id: 'create_service',
      title: 'Create your first Service',
      description: 'Add a service to your catalog',
      icon: Package,
      link: '/services/new',
      completed: services.length > 0,
    },
    {
      id: 'configure_monitoring',
      title: 'Set up Monitoring',
      description: 'Configure health checks for your services',
      icon: Activity,
      link: '/monitoring',
      completed: hasMonitoringConfigs,
    },
  ];

  const completedCount = checklist.filter(item => item.completed).length;
  const progress = (completedCount / checklist.length) * 100;
  const isComplete = completedCount === checklist.length;

  const handleDismiss = () => {
    if (currentAccountId) {
      setIsDismissed(true);
      useOnboardingStore.getState().dismissChecklistForAccount(currentAccountId);
    }
  };

  // Don't show banner if complete or dismissed
  if (isComplete || isDismissed) {
    return null;
  }

  // Find next incomplete item
  const nextItem = checklist.find(item => !item.completed);

  // Trophy/Achievement system
  const getTrophy = (count: number) => {
    if (count === 0) return { icon: Target, color: 'text-muted-foreground', label: 'Beginner' };
    if (count === 1) return { icon: Star, color: 'text-yellow-500', label: 'Getting Started' };
    if (count === 2) return { icon: Zap, color: 'text-orange-500', label: 'Making Progress' };
    if (count === 3) return { icon: Award, color: 'text-blue-500', label: 'Almost There' };
    if (count === 4) return { icon: Trophy, color: 'text-purple-500', label: 'Nearly Done' };
    return { icon: Trophy, color: 'text-yellow-500 animate-pulse', label: 'Champion' };
  };

  const trophy = getTrophy(completedCount);
  const TrophyIcon = trophy.icon;

  return (
    <div className={cn(
      "mt-4 mb-3 rounded-xl border-2 transition-all duration-300 shadow-md overflow-hidden relative",
      isComplete 
        ? "bg-gradient-to-r from-success/15 via-success/8 to-success/5 border-success/40 shadow-success/20" 
        : "bg-gradient-to-r from-primary/15 via-primary/8 to-primary/5 border-primary/40"
    )}>
      {/* Subtle inner */}
      <div className={cn(
        "absolute inset-0 opacity-50 pointer-events-none",
        isComplete 
          ? "bg-gradient-to-br from-success/10 to-transparent" 
          : "bg-gradient-to-br from-primary/10 to-transparent"
      )} />
      
      <div className="p-2 sm:p-3 md:p-3.5 relative z-10">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-3 lg:gap-4">
          {/* Left: Trophy & Title */}
          <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0 w-full lg:w-auto">
            <div className={cn(
              "flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-full shadow-lg transition-all flex-shrink-0",
              isComplete 
                ? "bg-success/20 shadow-success/30" 
                : "bg-primary/20"
            )}>
              <TrophyIcon className={cn("h-5 w-5 sm:h-6 sm:w-6 drop-shadow-sm", trophy.color)} />
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-sm sm:text-base">
                  {isComplete ? '🎉 Setup Complete!' : 'Getting Started Quest'}
                </h3>
                <Badge variant="secondary" className="text-xs">
                  {trophy.label}
                </Badge>
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground">
                {isComplete 
                  ? "You've unlocked all achievements! You're ready to monitor." 
                  : `Complete ${checklist.length - completedCount} more ${checklist.length - completedCount === 1 ? 'task' : 'tasks'} to unlock Champion status`}
              </p>
            </div>
          </div>

          {/* Middle: Progress & Steps */}
          <div className="hidden lg:flex items-center gap-3 flex-1">
            {checklist.map((item, index) => {
              const Icon = item.icon;
              const isNext = item.id === nextItem?.id;
              
              return (
                <Link
                  key={item.id}
                  to={item.link}
                  className={cn(
                    "relative flex flex-col items-center gap-1 p-2 rounded-lg transition-all group",
                    item.completed && "opacity-100",
                    !item.completed && !isNext && "opacity-40",
                    isNext && "opacity-100"
                  )}
                >
                  <div className={cn(
                    "flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all",
                    item.completed 
                      ? "bg-success/20 border-success text-success" 
                      : isNext
                      ? "bg-primary/20 border-primary text-primary animate-pulse"
                      : "bg-muted border-muted-foreground/20 text-muted-foreground"
                  )}>
                    {item.completed ? (
                      <CheckCircle2 className="h-5 w-5" />
                    ) : (
                      <Icon className="h-5 w-5" />
                    )}
                  </div>
                  <span className={cn(
                    "text-xs font-medium text-center max-w-[80px] truncate",
                    item.completed && "text-success",
                    isNext && !item.completed && "text-primary",
                    !item.completed && !isNext && "text-muted-foreground"
                  )}>
                    {item.title.replace('Create your first ', '').replace('Set up a ', '').replace('Add an ', '')}
                  </span>
                  
                  {/* Pointing hand for next item - appears after the item */}
                  {isNext && !item.completed && (
                    <div className="absolute -right-4 top-1/2 -translate-y-[60%] z-10 pointer-events-none">
                      <div className="text-xl animate-[slideLeft_1s_ease-in-out_infinite]">
                        👈
                      </div>
                    </div>
                  )}
                  
                  {/* Connector line */}
                  {index < checklist.length - 1 && (
                    <div className={cn(
                      "absolute left-full top-6 w-3 h-0.5 transition-all",
                      item.completed ? "bg-success" : "bg-muted"
                    )} />
                  )}
                </Link>
              );
            })}
          </div>

          {/* Right: Progress Bar & Actions */}
          <div className="flex items-center gap-2 sm:gap-3 w-full lg:w-auto justify-between lg:justify-end">
            <div className="hidden md:flex flex-col items-end gap-1 min-w-[120px]">
              <div className="flex items-center gap-2 text-sm font-medium">
                <span className={isComplete ? "text-success" : "text-primary"}>
                  {completedCount}/{checklist.length}
                </span>
                <span className="text-muted-foreground">completed</span>
              </div>
              <Progress value={progress} className="h-2 w-full" />
            </div>
            
            {isComplete ? (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleDismiss}
                className="h-8 w-8"
              >
                <X className="h-4 w-4" />
              </Button>
            ) : (
              nextItem && (
                <Button asChild size="sm" className="gap-2">
                  <Link to={nextItem.link}>
                    Next Step
                    <ChevronRight className="h-4 w-4" />
                  </Link>
                </Button>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
